import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import HeartbeatPulse from '@/components/mind/HeartbeatPulse';
import DirectiveCard from '@/components/mind/DirectiveCard';
import PrimitiveRadar from '@/components/mind/PrimitiveRadar';
import TensionSpectrum from '@/components/mind/TensionSpectrum';

export default function PresenceTab({ presence, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!presence) {
    return (
      <div className="text-center text-muted-foreground py-20">
        <p className="text-sm">No presence data available.</p>
        <p className="text-xs mt-1">The heartbeat needs to run at least once.</p>
      </div>
    );
  }

  const { heartbeat, attention, landscape } = presence;
  const directives = attention?.directives || [];
  const anchors = attention?.semantic_anchors || [];
  const primitives = landscape?.primitives || [];
  const tensions = landscape?.tensions || [];
  const emotionalLandscape = landscape?.emotional_landscape || null;
  const stats = landscape?.graph_stats || {};

  return (
    <div className="space-y-6">
      {/* The Pulse */}
      <section>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">The Pulse</h3>
        <HeartbeatPulse heartbeat={heartbeat} />
      </section>

      {/* Two-column: Directives + Landscape */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Attention Directives */}
        <section>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">
            What I'm Watching For
          </h3>
          {directives.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                No active directives yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {directives.slice(0, 10).map((d, i) => (
                <DirectiveCard key={i} directive={d} />
              ))}
            </div>
          )}

          {/* Semantic Anchors */}
          {anchors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Key Anchors</h4>
              <div className="flex flex-wrap gap-1.5">
                {anchors.map((a, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="text-[10px] font-normal"
                  >
                    {a.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Primitive Landscape */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-muted-foreground">
              How This Feels
            </h3>
            {emotionalLandscape && (
              <Badge variant="outline" className="text-[10px]">
                {emotionalLandscape}
              </Badge>
            )}
          </div>

          <Card>
            <CardContent className="p-4">
              <PrimitiveRadar primitives={primitives} />
            </CardContent>
          </Card>

          {/* Tension Spectrum */}
          {tensions.length > 0 && (
            <Card className="mt-3">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground">
                  Tension Axes
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <TensionSpectrum tensions={tensions} />
              </CardContent>
            </Card>
          )}

          {/* Graph Stats */}
          {(stats.total_people || stats.total_concepts || stats.total_insights) && (
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              {stats.total_people > 0 && <span>{stats.total_people} people</span>}
              {stats.total_concepts > 0 && <span>{stats.total_concepts} concepts</span>}
              {stats.total_insights > 0 && <span>{stats.total_insights} insights</span>}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
